import { useState, useEffect, Fragment } from 'react'
import axios from '../../api/client'
import { cn } from '../../utils'
import styles from './EngagementDashboard.module.css'

interface ActivityRecord {
  id: number
  user_id: number
  user_email: string
  action: string
  metadata: string | null
  ip: string | null
  created_at: string
}

interface Kpi { value: number; prev: number }
interface RetentionBucket { pct: number; cohort: number }

interface Analytics {
  range: string
  days: number
  kpis: {
    dau: Kpi; wau: Kpi; mau: Kpi; stickiness: Kpi
    newUsers: Kpi; returningUsers: Kpi; activeUsers: Kpi
    sessions: Kpi; avgActionsPerSession: Kpi; avgSessionMinutes: Kpi
  }
  activeOverTime: { date: string; users: number; events: number }[]
  features: { topPages: { page: string; views: number; users: number }[]; releaseViews: number }
  retention: { d1: RetentionBucket; d7: RetentionBucket; d30: RetentionBucket }
  funnel: { step: string; count: number }[]
  powerUsers: { email: string; events: number; lastActive: string; daysActive: number }[]
  heatmap: number[][]
  acquisition: { source: string; count: number }[]
  geo: { country: string; count: number }[]
  platforms: { platform: string; count: number }[]
}

const RANGES = [
  { k: '7d', label: '7 days' },
  { k: '30d', label: '30 days' },
  { k: '90d', label: '90 days' },
]

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const ACTION_META: Record<string, { label: string; icon: string; cls: string }> = {
  register:       { label: 'Signed up',       icon: 'bi-person-plus',     cls: styles.aGreen },
  login:          { label: 'Logged in',       icon: 'bi-box-arrow-in-right', cls: styles.aBlue },
  login_telegram: { label: 'Telegram login',  icon: 'bi-telegram',        cls: styles.aBlue },
  discogs_connect:{ label: 'Connected Discogs', icon: 'bi-vinyl',         cls: styles.aPurple },
  discogs_sync:   { label: 'Synced Discogs',   icon: 'bi-arrow-repeat',   cls: styles.aPurple },
  release_view:   { label: 'Viewed release',   icon: 'bi-disc',           cls: styles.aNeutral },
  page_view:      { label: 'Viewed page',      icon: 'bi-window',         cls: styles.aNeutral },
}

function fmt(n: number): string {
  return n.toLocaleString()
}

function relTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr.replace(' ', 'T') + 'Z').getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function Delta({ value, prev, invert }: { value: number; prev: number; invert?: boolean }) {
  if (prev === 0) {
    if (value === 0) return <span className={styles.deltaFlat}>—</span>
    return <span className={styles.deltaUp}><i className="bi bi-arrow-up" /> new</span>
  }
  const change = Math.round(((value - prev) / prev) * 100)
  if (change === 0) return <span className={styles.deltaFlat}>0%</span>
  const positive = invert ? change < 0 : change > 0
  return (
    <span className={positive ? styles.deltaUp : styles.deltaDown}>
      <i className={`bi bi-arrow-${change > 0 ? 'up' : 'down'}`} />
      {Math.abs(change)}%
    </span>
  )
}

function KpiCard({ label, value, prev, suffix, hint, showDelta = true }: {
  label: string; value: number; prev?: number; suffix?: string; hint?: string; showDelta?: boolean
}) {
  return (
    <div className={styles.kpi} title={hint}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>
        {fmt(value)}{suffix && <span className={styles.kpiSuffix}>{suffix}</span>}
      </div>
      {showDelta && prev !== undefined && (
        <div className={styles.kpiDelta}><Delta value={value} prev={prev} /> <span className={styles.kpiDeltaHint}>vs prev</span></div>
      )}
    </div>
  )
}

function ActiveUsersChart({ data }: { data: Analytics['activeOverTime'] }) {
  const W = 720, H = 180, padX = 4, padY = 12
  const maxUsers = Math.max(1, ...data.map(d => d.users))
  const maxEvents = Math.max(1, ...data.map(d => d.events))
  const n = data.length
  const xStep = (W - padX * 2) / Math.max(1, n - 1)
  const xy = (i: number, v: number, max: number) => ({
    x: padX + i * xStep,
    y: H - padY - (v / max) * (H - padY * 2),
  })
  const linePts = data.map((d, i) => { const p = xy(i, d.users, maxUsers); return `${p.x},${p.y}` }).join(' ')
  const areaPts = `${padX},${H - padY} ${linePts} ${padX + (n - 1) * xStep},${H - padY}`
  const barW = Math.max(1, (W - padX * 2) / n - 1)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.chart} preserveAspectRatio="none" role="img" aria-label="Active users over time">
      {/* event volume bars (faint) */}
      {data.map((d, i) => {
        const p = xy(i, d.events, maxEvents)
        return <rect key={i} x={padX + i * xStep - barW / 2} y={p.y} width={barW} height={H - padY - p.y} className={styles.chartBar} />
      })}
      <polygon points={areaPts} className={styles.chartArea} />
      <polyline points={linePts} className={styles.chartLine} />
      {data.map((d, i) => {
        const p = xy(i, d.users, maxUsers)
        return <circle key={i} cx={p.x} cy={p.y} r={2} className={styles.chartDot}><title>{`${d.date}: ${d.users} active · ${d.events} events`}</title></circle>
      })}
    </svg>
  )
}

function Heatmap({ matrix }: { matrix: number[][] }) {
  const max = Math.max(1, ...matrix.flat())
  // Peak cell
  let peak = { dow: 0, hour: 0, c: 0 }
  matrix.forEach((row, d) => row.forEach((c, h) => { if (c > peak.c) peak = { dow: d, hour: h, c } }))
  return (
    <div>
      <div className={styles.heatmap}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className={styles.heatHourLabel}>{h % 6 === 0 ? h : ''}</div>
        ))}
        {matrix.map((row, d) => (
          <Fragment key={d}>
            <div className={styles.heatDowLabel}>{DOW[d]}</div>
            {row.map((c, h) => (
              <div
                key={`${d}-${h}`}
                className={styles.heatCell}
                style={{ opacity: c === 0 ? 0.06 : 0.18 + 0.82 * (c / max) }}
                title={`${DOW[d]} ${String(h).padStart(2, '0')}:00 UTC — ${c} events`}
              />
            ))}
          </Fragment>
        ))}
      </div>
      <div className={styles.heatPeak}>
        Peak: <strong>{DOW[peak.dow]} {String(peak.hour).padStart(2, '0')}:00 UTC</strong> ({peak.c} events) · times in UTC
      </div>
    </div>
  )
}

export function EngagementDashboard({ activity }: { activity: ActivityRecord[] }) {
  const [range, setRange] = useState('30d')
  const [data, setData] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    axios.get(`/users/analytics?range=${range}`)
      .then(({ data }) => { if (!cancelled) { setData(data); setLoading(false) } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false) } })
    return () => { cancelled = true }
  }, [range])

  const k = data?.kpis
  const funnelMax = data ? Math.max(1, ...data.funnel.map(f => f.count)) : 1
  const featureMax = data ? Math.max(1, ...data.features.topPages.map(p => p.views)) : 1
  const acqMax = data ? Math.max(1, ...data.acquisition.map(a => a.count)) : 1
  const geoMax = data ? Math.max(1, ...data.geo.map(gc => gc.count)) : 1
  const platformMax = data ? Math.max(1, ...data.platforms.map(p => p.count)) : 1

  return (
    <section className={styles.dashboard}>
      <div className={styles.header}>
        <h2 className={styles.title}>Engagement</h2>
        <div className={styles.rangeTabs}>
          {RANGES.map(r => (
            <button
              key={r.k}
              className={cn(styles.rangeTab, range === r.k && styles.rangeTabActive)}
              onClick={() => setRange(r.k)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className={styles.stateMsg}><i className="bi bi-exclamation-triangle" /> Failed to load analytics</div>
      ) : loading || !k ? (
        <div className={styles.stateMsg}><i className="bi bi-arrow-repeat spin" /> Loading analytics…</div>
      ) : (
        <>
          {/* Executive overview */}
          <div className={styles.kpiGrid}>
            <KpiCard label="Daily active" value={k.dau.value} prev={k.dau.prev} hint="Distinct non-admin users active in the last 24h" />
            <KpiCard label="Weekly active" value={k.wau.value} prev={k.wau.prev} hint="Last 7 days" />
            <KpiCard label="Monthly active" value={k.mau.value} prev={k.mau.prev} hint="Last 30 days" />
            <KpiCard label="Stickiness" value={k.stickiness.value} prev={k.stickiness.prev} suffix="%" hint="DAU / MAU — how often monthly users return daily" />
            <KpiCard label="New users" value={k.newUsers.value} prev={k.newUsers.prev} hint={`Signed up in the last ${data.days} days`} />
            <KpiCard label="Returning" value={k.returningUsers.value} showDelta={false} hint="Active in range, signed up earlier" />
            <KpiCard label="Sessions" value={k.sessions.value} prev={k.sessions.prev} hint="30-min inactivity gap" />
            <KpiCard label="Avg session" value={k.avgSessionMinutes.value} suffix=" min" showDelta={false} hint={`${k.avgActionsPerSession.value} actions/session`} />
          </div>

          {/* Active users over time */}
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <span className={styles.panelTitle}>Active users over time</span>
              <span className={styles.panelMeta}>line = daily active users · bars = events</span>
            </div>
            <ActiveUsersChart data={data.activeOverTime} />
          </div>

          <div className={styles.row2}>
            {/* Activation funnel */}
            <div className={styles.panel}>
              <div className={styles.panelHead}><span className={styles.panelTitle}>Activation funnel</span></div>
              <div className={styles.funnel}>
                {data.funnel.map((f, i) => {
                  const prev = i > 0 ? data.funnel[i - 1].count : f.count
                  const ofTop = funnelMax > 0 ? Math.round((f.count / funnelMax) * 100) : 0
                  const drop = i > 0 && prev > 0 ? Math.round((1 - f.count / prev) * 100) : 0
                  return (
                    <div key={f.step} className={styles.funnelStep}>
                      <div className={styles.funnelLabel}>
                        <span>{f.step}</span>
                        <span className={styles.funnelCount}>{fmt(f.count)} <span className={styles.funnelPct}>{ofTop}%</span></span>
                      </div>
                      <div className={styles.funnelTrack}>
                        <div className={styles.funnelFill} style={{ width: `${ofTop}%` }} />
                      </div>
                      {i > 0 && drop > 0 && <div className={styles.funnelDrop}><i className="bi bi-arrow-down-right" /> {drop}% drop-off</div>}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Retention */}
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <span className={styles.panelTitle}>Rolling retention</span>
                <span className={styles.panelMeta}>returned ≥ N days after signup</span>
              </div>
              <div className={styles.retentionGrid}>
                {([['D1', data.retention.d1], ['D7', data.retention.d7], ['D30', data.retention.d30]] as const).map(([label, b]) => (
                  <div key={label} className={styles.retCard}>
                    <div className={styles.retLabel}>{label}</div>
                    <div className={styles.retPct}>{b.pct}%</div>
                    <div className={styles.retBarTrack}><div className={styles.retBarFill} style={{ height: `${b.pct}%` }} /></div>
                    <div className={styles.retCohort}>{b.cohort} users</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.row2}>
            {/* Feature usage */}
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <span className={styles.panelTitle}>Feature usage</span>
                <span className={styles.panelMeta}>page views · {fmt(data.features.releaseViews)} release opens</span>
              </div>
              {data.features.topPages.length === 0 ? (
                <div className={styles.emptyInline}>No page views in this range</div>
              ) : (
                <div className={styles.bars}>
                  {data.features.topPages.map(p => (
                    <div key={p.page} className={styles.barRow}>
                      <span className={styles.barLabel}>{p.page}</span>
                      <div className={styles.barTrack}>
                        <div className={styles.barFill} style={{ width: `${Math.round((p.views / featureMax) * 100)}%` }} />
                      </div>
                      <span className={styles.barValue}>{fmt(p.views)} <span className={styles.barSub}>· {p.users}u</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Power users */}
            <div className={styles.panel}>
              <div className={styles.panelHead}><span className={styles.panelTitle}>Power users</span><span className={styles.panelMeta}>top by events</span></div>
              {data.powerUsers.length === 0 ? (
                <div className={styles.emptyInline}>No activity in this range</div>
              ) : (
                <table className={styles.leaderboard}>
                  <thead><tr><th>User</th><th>Events</th><th>Active days</th><th>Last seen</th></tr></thead>
                  <tbody>
                    {data.powerUsers.map((u, i) => (
                      <tr key={u.email}>
                        <td><span className={styles.rank}>{i + 1}</span>{u.email}</td>
                        <td>{fmt(u.events)}</td>
                        <td>{u.daysActive}</td>
                        <td className={styles.muted}>{relTime(u.lastActive)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Acquisition + geo + platform */}
          <div className={styles.row3}>
            <div className={styles.panel}>
              <div className={styles.panelHead}><span className={styles.panelTitle}>Acquisition</span><span className={styles.panelMeta}>where signups come from</span></div>
              {data.acquisition.length === 0 ? (
                <div className={styles.emptyInline}>No signups yet</div>
              ) : (
                <div className={styles.bars}>
                  {data.acquisition.map(a => (
                    <div key={a.source} className={styles.barRow}>
                      <span className={styles.barLabel}>{a.source}</span>
                      <div className={styles.barTrack}><div className={styles.barFill} style={{ width: `${Math.round((a.count / acqMax) * 100)}%` }} /></div>
                      <span className={styles.barValue}>{fmt(a.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.panel}>
              <div className={styles.panelHead}><span className={styles.panelTitle}>Top countries</span><span className={styles.panelMeta}>by signups</span></div>
              {data.geo.length === 0 ? (
                <div className={styles.emptyInline}>No country data yet</div>
              ) : (
                <div className={styles.bars}>
                  {data.geo.map(gc => (
                    <div key={gc.country} className={styles.barRow}>
                      <span className={styles.barLabel}>{gc.country}</span>
                      <div className={styles.barTrack}><div className={styles.barFill} style={{ width: `${Math.round((gc.count / geoMax) * 100)}%` }} /></div>
                      <span className={styles.barValue}>{fmt(gc.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.panel}>
              <div className={styles.panelHead}><span className={styles.panelTitle}>Platforms</span><span className={styles.panelMeta}>active users in range</span></div>
              {data.platforms.length === 0 ? (
                <div className={styles.emptyInline}>No platform data yet</div>
              ) : (
                <div className={styles.bars}>
                  {data.platforms.map(p => (
                    <div key={p.platform} className={styles.barRow}>
                      <span className={styles.barLabel}>{p.platform}</span>
                      <div className={styles.barTrack}><div className={styles.barFill} style={{ width: `${Math.round((p.count / platformMax) * 100)}%` }} /></div>
                      <span className={styles.barValue}>{fmt(p.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Heatmap */}
          <div className={styles.panel}>
            <div className={styles.panelHead}><span className={styles.panelTitle}>Activity heatmap</span><span className={styles.panelMeta}>when users are active</span></div>
            <Heatmap matrix={data.heatmap} />
          </div>

          {/* Live feed */}
          <div className={styles.panel}>
            <div className={styles.panelHead}><span className={styles.panelTitle}>Live activity</span><span className={styles.panelMeta}>most recent events</span></div>
            <div className={styles.feed}>
              {activity.length === 0 ? (
                <div className={styles.emptyInline}>No activity yet</div>
              ) : activity.slice(0, 40).map(a => {
                const meta = a.metadata ? (() => { try { return JSON.parse(a.metadata!) } catch { return {} } })() : {}
                const m = ACTION_META[a.action] ?? { label: a.action, icon: 'bi-dot', cls: styles.aNeutral }
                const detail = a.action === 'release_view' ? meta.release_title
                  : a.action === 'page_view' ? meta.page
                  : a.action === 'discogs_sync' ? meta.type
                  : a.action === 'discogs_connect' ? meta.discogs_username
                  : null
                return (
                  <div key={a.id} className={styles.feedRow}>
                    <span className={cn(styles.feedIcon, m.cls)}><i className={`bi ${m.icon}`} /></span>
                    <span className={styles.feedUser}>{a.user_email}</span>
                    <span className={styles.feedAction}>{m.label}</span>
                    {detail && <span className={styles.feedDetail}>{detail}</span>}
                    <span className={styles.feedTime}>{relTime(a.created_at)}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </section>
  )
}
